<template>
  <parent>
    <div class="errorpages-form-wrapper h-100">
      <template v-if="loading">
        <div class="loading">
          <GjIcon name="Loader" />
        </div>
      </template>
      <validation-observer
        ref="rules"
        v-slot="{ invalid }"
        tag="div"
        class="h-100"
      >
        <form-wrapper v-if="!loading">
          <template v-slot:body>
            <paper-simple>
              <input-field
                label="HTTP Error Code"
                label-position="top"
                info-position="label"
                :info="messages.httpErrorCode"
                @openInfo="
                  showInfo(
                    messages.httpErrorCode.title,
                    messages.httpErrorCode.message,
                  )
                "
              >
                <custom-select
                  v-model="errorpage.httpErrorCode"
                  :options="getOptions(errorpage.httpErrorCodeOptions)"
                  @change="changeResponseCode"
                />
              </input-field>

              <input-field
                label="Error Caching Minimum TTL (seconds)"
                label-position="top"
                info-position="label"
                :info="messages.errorCachingMinimumTtl"
                @openInfo="
                  showInfo(
                    messages.errorCachingMinimumTtl.title,
                    messages.errorCachingMinimumTtl.message,
                  )
                "
              >
                <b-form-input
                  v-model="errorpage.errorCachingMinimumTtl"
                  placeholder="1"
                />
              </input-field>
            </paper-simple>

            <paper-simple>
              <custom-input-radio
                v-model="errorpage.customizeErrorResponse"
                label="Customize Error Response"
                :info="messages.customizeErrorResponse"
                :options="checkboxOptions"
                @openInfo="
                  showInfo(
                    messages.customizeErrorResponse.title,
                    messages.customizeErrorResponse.message,
                  )
                "
              />
              <template v-if="errorpage.customizeErrorResponse">
                <div class="top-fade-in">
                  <validation-provider
                    #default="{ errors }"
                    name="Response page"
                    rules="required"
                    class="w-100"
                  >
                    <input-field
                      label="Response Page"
                      label-position="top"
                      info-position="label"
                      :error="errors.length ? errors[0] : null"
                    >
                      <div class="w-100">
                        <b-form-textarea
                          id="textarea-default"
                          v-model="errorpage.responsePage"
                          placeholder="Add HTML here..."
                          rows="8"
                        />
                      </div>
                    </input-field>
                    <custom-alert
                      icon-name="Info"
                      items-center
                      class="mt-1"
                    >
                      {{ messages.responsePage.message }}
                    </custom-alert>
                  </validation-provider>

                  <input-field
                    label="HTTP Response Code"
                    label-position="top"
                    info-position="label"
                    :info="messages.httpResponseCode"
                    @openInfo="
                      showInfo(
                        messages.httpResponseCode.title,
                        messages.httpResponseCode.message,
                      )
                    "
                  >
                    <custom-select
                      v-model="errorpage.httpResponseCode"
                      :options="getOptions(errorpage.httpResponseCodeOptions)"
                      @change="responseCodeModified = true"
                    />
                  </input-field>
                </div>
              </template>
            </paper-simple>
          </template>

          <template v-slot:footer>
            <b-button
              variant="bg-white"
              @click="$emit('close')"
            >
              Cancel
            </b-button>
            <button-loading
              variant="bg-primary"
              class="ml-16"
              :disabled="invalid"
              :spinning="details ? updatingErrorpage : addingErrorpage"
              @click="onCreate"
            >
              {{ details ? 'Update' : 'Create' }}
            </button-loading>
          </template>
        </form-wrapper>
      </validation-observer>
      <info
        v-if="openInfo"
        :title="infoTitle"
        :message="infoMessage"
        @close="openInfo = false"
      />
    </div>
  </parent>
</template>
<script>
import messages from '../../messages'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import { required } from '@validations'
import redirectError from '@/services/redirectError'

export default {
  name: 'ErrorPagesForm',
  components: { CustomInputRadio, ValidationProvider, ValidationObserver },
  props: {
    row: {
      default: null,
      type: Object,
    },
    details: {
      type: Boolean,
      default: false,
    },
    errorpageId: {
      type: Number,
      default: null,
    },
    distributionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      required,
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      loading: true,
      errorpage: {},
      selected: null,
      selectedCheckbox: false,
      checkboxOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      responseCodeModified: false,
    }
  },
  computed: {
    httpErrorCodeOptions() {
      return this.$store.state.cdn.httpErrorCodeOptions
    },
    updatingErrorpage() {
      return this.$store.state.cdn.updatingErrorpage
    },
    addingErrorpage() {
      return this.$store.state.cdn.addingErrorpage
    },
  },
  created() {
    this.$store
      .dispatch('cdn/getErrorPage', {
        distributionId: this.distributionId,
        id: this.details ? this.errorpageId : 0,
      })
      .then(() => {
        this.errorpage = this.$store.state.cdn.errorpage
        this.loading = false
        if (!this.details) {
          this.errorpage.distributionId = this.$route.params.id
          const errorId = this.errorpage.httpErrorCodeOptions[0].id
          this.errorpage.httpErrorCode = errorId
          this.errorpage.httpResponseCode = errorId
        }
      })
      .catch(err => redirectError(err, this.$router, this.$route.path))
  },
  methods: {
    onCreate() {
      const localErrorPage = {
        id: this.errorpageId,
        distributionId: this.errorpage.distributionId,
        httpErrorCode: this.errorpage.httpErrorCode,
        errorCachingMinimumTtl: this.errorpage.errorCachingMinimumTtl,
        customizeErrorResponse: this.errorpage.customizeErrorResponse,
        responsePage: this.errorpage.responsePage,
        httpResponseCode: this.errorpage.httpResponseCode,
      }
      this.$emit('submit', localErrorPage)
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
    changeResponseCode() {
      this.errorpage.httpResponseCode = this.responseCodeModified
        ? this.errorpage.httpResponseCode
        : this.errorpage.httpErrorCode
    },
  },
}
</script>
<style lang="scss" scoped>
.errorpages-form-wrapper {
  ::v-deep {
    .form-wrapper {
      &__body {
        > div {
          margin: auto;
        }
      }
    }
    .card.paper {
      .card-body {
        padding-bottom: 0;
      }
    }

    .info-assistant {
      margin-left: auto;
    }
  }
}
</style>
